import React, { useRef, useState } from 'react';
import {
  PencilSquareIcon,
  DocumentArrowUpIcon,
  ArrowPathIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import {
  DocumentArrowUpIcon as DocumentArrowUpIconSolid,
  Cog6ToothIcon as Cog6ToothIconSolid,
  ChevronDownIcon,
} from '@heroicons/react/24/solid';
import CardContainer from '../moduleComponents/CardContainer';
import GeneralCardMassiveUpdate from './viewComponents/Manage/GeneralCardMassiveUpdate';
import CustomAccountsStatus from './viewComponents/Manage/CustomAccountsStatus';
import CustomAccountsDownload from './viewComponents/Manage/CustomAccountsDownload';
import CustomFlowsClassify from './viewComponents/Manage/CustomFlowsClassify';
import CustomFlowsUpload from './viewComponents/Manage/CustomFlowsUpload';
import CardFlowPreliminaryTransactions from './viewComponents/Manage/CardFlowPreliminaryTransactions';

/** Accordion matching Assets Custom Update — panels stay mounted; `hidden` when collapsed. */
const FlowCustomUpdateWorkflowAccordion = ({ accountsCheckRef, accountsDownloadRef, onPreliminaryMerged }) => {
  const [open, setOpen] = useState({ 1: false, 2: false, 3: false, 4: false });
  const toggle = (id) => setOpen((prev) => ({ ...prev, [id]: !prev[id] }));

  const sections = [
    {
      id: 1,
      title: '1. Accounts Temporary Info',
      body: <CustomAccountsStatus ref={accountsCheckRef} />,
    },
    {
      id: 2,
      title: '2. Download Account Flows',
      body: (
        <CustomAccountsDownload ref={accountsDownloadRef} onPreliminaryMerged={onPreliminaryMerged} />
      ),
    },
    {
      id: 3,
      title: '3. Classify Flows',
      body: <CustomFlowsClassify />,
    },
    {
      id: 4,
      title: '4. Upload Financial Flows',
      body: <CustomFlowsUpload />,
    },
  ];

  return (
    <div className="mt-1 border-t border-gray-200 pt-5">
      <h3 className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
        Workflow details
      </h3>
      <div className="flex flex-col gap-2.5">
        {sections.map(({ id, title, body }) => {
          const isOpen = open[id];
          return (
            <div key={id} className="overflow-hidden">
              <button
                type="button"
                onClick={() => toggle(id)}
                aria-expanded={isOpen}
                aria-controls={`flow-custom-update-acc-panel-${id}`}
                id={`flow-custom-update-acc-head-${id}`}
                className="flex w-full items-center justify-between gap-2 rounded-lg bg-gray-100 px-3 py-1.5 text-left text-sm font-medium text-gray-800 hover:bg-gray-200/70"
              >
                <span>{title}</span>
                <ChevronDownIcon
                  className={`h-5 w-5 shrink-0 text-gray-500 transition-transform ${
                    isOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>
              <div
                id={`flow-custom-update-acc-panel-${id}`}
                role="region"
                aria-labelledby={`flow-custom-update-acc-head-${id}`}
                className={isOpen ? 'bg-white py-3.5' : 'hidden'}
              >
                {body}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ViewFlowManage = () => {
  // Refs para permitir refresh dos dados nos cards filhos
  const accountsCheckRef = useRef(null);
  const accountsDownloadRef = useRef(null);
  const preliminaryCardRef = useRef(null);

  return (
    <div className="grid grid-cols-1 w-full gap-2">

      {/* Card Instructions */}
      <CardContainer title="Instructions" collapsible={true} defaultExpanded={false}>
        <div className="space-y-4 text-sm text-gray-700">
          <p className="font-semibold">
            This section has the tools to manage your Flow. Choose the option that best fits your needs:
          </p>

          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-800 flex items-center justify-center mt-0.5">
                <PencilSquareIcon className="h-4 w-4 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-800 mb-1">Insert Manually</h3>
                <p className="text-sm text-gray-600">
                  Full precision. Add each expense individually using the ( + ) button.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-800 flex items-center justify-center mt-0.5">
                <DocumentArrowUpIcon className="h-4 w-4 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-800 mb-1">Excel Import</h3>
                <p className="text-sm text-gray-600">
                  Upload all entries at once. Download the Excel template below, fill it with your data and upload to your records.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-400 flex items-center justify-center mt-0.5">
                <ArrowPathIcon className="h-4 w-4 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-400 mb-1">Automatic Sync (in development)</h3>
                <p className="text-sm text-gray-400">
                  Uses Open Finance to automatically download, categorize, and update.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-400 flex items-center justify-center mt-0.5">
                <Cog6ToothIcon className="h-4 w-4 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-400 mb-1">Custom Update (in development)</h3>
                <p className="text-sm text-gray-400">
                  A hybrid method that mixes the easy data retrieval of Open Finance with the flexibility of Excel Import.
                </p>
              </div>
            </div>
          </div>
        </div>
      </CardContainer>

      {/* Card Massive Update — shell alinhado a Assets Manage */}
      <div className="h-auto bg-white shadow rounded-xl p-4 flex flex-col gap-4 flex-1">
        <div className="bg-green-800 border-b border-gray-200 -m-4 mb-4 rounded-t-xl">
          <div className="px-3 py-2 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <DocumentArrowUpIconSolid className="h-4 w-4 text-white shrink-0" />
              <h2 className="text-sm font-medium text-white">Import Options</h2>
            </div>
          </div>
        </div>

        <div className="w-full">
          <GeneralCardMassiveUpdate />
        </div>
      </div>

      {/* Card Batch Update — shell alinhado a Assets Manage */}
      <div className="h-auto bg-white shadow rounded-xl p-4 flex flex-col gap-4 flex-1">
        <div className="bg-green-800 border-b border-gray-200 -m-4 mb-4 rounded-t-xl">
          <div className="px-3 py-2 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Cog6ToothIconSolid className="h-4 w-4 text-white shrink-0" />
              <h2 className="text-sm font-medium text-white">Custom Update</h2>
            </div>
          </div>
        </div>

        <FlowCustomUpdateWorkflowAccordion
          accountsCheckRef={accountsCheckRef}
          accountsDownloadRef={accountsDownloadRef}
          onPreliminaryMerged={() => preliminaryCardRef.current?.refresh?.()}
        />
      </div>

      <CardFlowPreliminaryTransactions ref={preliminaryCardRef} />

    </div>
  );
};

export default ViewFlowManage;
